import { FormProvider, useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import ThankYouPageSection from "../reusable-form-section/thank-you-page-section";
import {
  updateGlobalReclaimConfig,
  updateDigitalThankyouPage
} from "../../services/api-declaration";
import { showSuccess, showError } from "../../services/toast-service";
import {
  CustomCard,
  FieldSet,
  HorizontalDivider,
  Legend
} from "../common-styling";
import TextArea from "../reusable-form-fields/text-area";
import PreventReload from "../funnel-form/prevent-reload";
import { FUNNEL_DESIGNS } from "../strings";
import { thankYouPageObj } from "../common-confgs/common-vars";
import {
  setGlobalReclaimConfig,
  setDigitalThankyouPage
} from "../../store/slices/auth";

const GlobalThankYouTemplateForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalReclaimConfig = useSelector(
    (state) => state.auth.user.workspace.globalReclaimConfig
  );
  const digitalThankyouPage = useSelector(
    (state) => state.auth.user.workspace.digitalThankyouPage
  );

  // separate loading states
  const [isThankyouSubmitting, setIsThankyouSubmitting] = useState(false);
  const [isDigitalThankyouSubmitting, setIsDigitalThankyouSubmitting] =
    useState(false);
  const [isLeadSubmitting, setIsLeadSubmitting] = useState(false);

  // Form 1
  const thankyouMethods = useForm({
    defaultValues: {
      thankyouPage: thankYouPageObj,
      scriptThankyou: "",
      funnelDesign: FUNNEL_DESIGNS.CLASSIC
    }
  });

  // Form 2
  const leadMethods = useForm({
    defaultValues: {
      leadServiceIndividualNote: ""
    }
  });

  // Form 3
  const digitalThankyouMethods = useForm({
    defaultValues: {
      thankyouPage: {
        thankyouPageHeadline: "",
        thankyouPageText: "",
        greetingPhrase: ""
      }
    }
  });

  const {
    handleSubmit: handleThankyouSubmit,
    reset: resetThankyou,
    register: registerThankyou
  } = thankyouMethods;

  const {
    handleSubmit: handleLeadSubmit,
    reset: resetLead,
    register: registerLead
  } = leadMethods;

  const {
    handleSubmit: handleDigitalThankyouSubmit,
    reset: resetDigitalThankyou
  } = digitalThankyouMethods;

  useEffect(() => {
    if (globalReclaimConfig) {
      const { thankyouPage, scriptThankyou, leadServiceIndividualNote } =
        globalReclaimConfig;

      // Fill Thankyou form
      resetThankyou({
        thankyouPage,
        scriptThankyou,
        funnelDesign: FUNNEL_DESIGNS.CLASSIC
      });

      // Fill Lead form
      resetLead({
        leadServiceIndividualNote
      });
    }
    if (digitalThankyouPage) {
      // Fill Digital Thankyou form
      resetDigitalThankyou({
        thankyouPage: digitalThankyouPage
      });
    }
  }, [
    globalReclaimConfig,
    digitalThankyouPage,
    resetThankyou,
    resetLead,
    resetDigitalThankyou
  ]);

  // Submit Thankyou
  const onSubmitThankyou = async (values) => {
    try {
      setIsThankyouSubmitting(true);

      const payload = {
        thankyouPage: values.thankyouPage,
        scriptThankyou: values.scriptThankyou
      };

      const response = await updateGlobalReclaimConfig({
        globalReclaimConfig: payload
      });
      dispatch(setGlobalReclaimConfig(payload));

      showSuccess(
        response?.data?.message ||
          "Global thank-you template updated successfully!"
      );
    } catch (error) {
      console.error(error);
      showError(
        error?.response?.data?.message || "Failed to update thank-you template"
      );
    } finally {
      setIsThankyouSubmitting(false);
    }
  };

  // Submit Lead Service
  const onSubmitLead = async (values) => {
    try {
      setIsLeadSubmitting(true);

      const payload = {
        leadServiceIndividualNote: values.leadServiceIndividualNote
      };

      const response = await updateGlobalReclaimConfig({
        globalReclaimConfig: payload
      });
      dispatch(setGlobalReclaimConfig(payload));

      showSuccess(
        response?.data?.message || "Lead service note updated successfully!"
      );
    } catch (error) {
      console.error(error);
      showError(
        error?.response?.data?.message || "Failed to update lead service note"
      );
    } finally {
      setIsLeadSubmitting(false);
    }
  };

  // Submit DigitalThankyou
  const onSubmitDigitalThankyou = async (values) => {
    try {
      setIsDigitalThankyouSubmitting(true);
      const payload = values.thankyouPage;
      const response = await updateDigitalThankyouPage(payload);
      dispatch(setDigitalThankyouPage(payload));
      showSuccess(
        response?.data?.message ||
          "Digital thank-you template updated successfully!"
      );
    } catch (error) {
      console.error(error);
      showError(
        error?.response?.data?.message ||
          "Failed to update digital thank-you template"
      );
    } finally {
      setIsDigitalThankyouSubmitting(false);
    }
  };

  return (
    <>
      {/* Thank You Form */}
      <FormProvider {...thankyouMethods}>
        <form onSubmit={handleThankyouSubmit(onSubmitThankyou)}>
          <PreventReload />
          <CustomCard className="my-4">
            <div className="m-3">
              <ThankYouPageSection global />

              <HorizontalDivider />

              <div className="py-3 px-0">
                <FieldSet>
                  <Legend>
                    <h6 className="mb-0 fw-bold">{t("script")}</h6>
                  </Legend>
                  <TextArea
                    label={t("thankyouPage")}
                    name="scriptThankyou"
                    register={registerThankyou}
                  />
                </FieldSet>
              </div>

              <HorizontalDivider />

              <div className="mb-3 px-0">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isThankyouSubmitting}
                >
                  {isThankyouSubmitting ? "Saving..." : t("common.save")}{" "}
                  {t("common.template")}
                </Button>
              </div>
            </div>
          </CustomCard>
        </form>
      </FormProvider>

      {/* Lead Service Form */}
      <FormProvider {...leadMethods}>
        <form onSubmit={handleLeadSubmit(onSubmitLead)}>
          <PreventReload />
          <CustomCard className="my-4">
            <div className="m-3">
              <div className="py-3 px-0">
                <FieldSet>
                  <Legend>
                    <h6 className="mb-0 fw-bold">{t("leadService")}</h6>
                  </Legend>
                  <TextArea
                    label={t(
                      "funnelIntegration.leadService.individualNoteLabel"
                    )}
                    name="leadServiceIndividualNote"
                    placeholder={`${t("common.enter")} ${t(
                      "funnelIntegration.leadService.individualNoteLabel"
                    )}`}
                    register={registerLead}
                  />
                </FieldSet>
              </div>

              <HorizontalDivider />

              <div className="mb-3 px-0">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isLeadSubmitting}
                >
                  {isLeadSubmitting ? "Saving..." : t("common.save")}
                </Button>
              </div>
            </div>
          </CustomCard>
        </form>
      </FormProvider>

      {/* Digital Thank You Form */}
      <FormProvider {...digitalThankyouMethods}>
        <form onSubmit={handleDigitalThankyouSubmit(onSubmitDigitalThankyou)}>
          <PreventReload />
          <CustomCard className="my-4">
            <div className="m-3">
              <ThankYouPageSection digital />

              <HorizontalDivider />

              <div className="mb-3 px-0">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isDigitalThankyouSubmitting}
                >
                  {isDigitalThankyouSubmitting ? "Saving..." : t("common.save")}{" "}
                  {t("common.template")}
                </Button>
              </div>
            </div>
          </CustomCard>
        </form>
      </FormProvider>
    </>
  );
};

export default GlobalThankYouTemplateForm;
